import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Footer from './Footer';

const founder = {
  name: 'Ayush Chaudhary',
  role: 'CEO & Entrepreneur',
  photo: '/ayush-photo.png',
  bio: 'Ayush is the creator of Panda AI. He built the first version as a side project and grew it into a product used by hundreds of thousands of Android users. He leads product vision and overall roadmap.',
  credentials: ['B.Tech — IIT Roorkee', 'Software Engineer — Pulse Lab', 'SDE Intern — True Beacon', 'Vice Chair — ACM Student Chapter, IITR'],
  linkedin: 'https://www.linkedin.com/in/ayush0chaudhary/',
  twitter: 'https://x.com/Ayush0Chaudhary',
  github: 'https://github.com/Ayush0Chaudhary',
};

const team = [
  {
    name: 'Ansh Chaudhary',
    role: 'Growth & Product',
    photo: '/ansh-photo.png',
    bio: 'Ansh bridges the gap between engineering and the community. He balances technical contributions with managing the social media presence and direct user engagement.',
    credentials: ['NSUT, Delhi (2024–28)', 'Texas Instruments Byte Program fellow', "D'Code NSUT"],
    linkedin: 'https://www.linkedin.com/in/ansh-chaudhary-451898349/',
    github: 'https://github.com/Ansh-12345',
  },
  {
    name: 'Usman Khatri',
    role: 'Software Engineer',
    photo: '/usman-photo.png',
    bio: 'Usman builds large-scale data transformation engines and secure financial APIs. He drives core system architecture and cloud integration for Panda.',
    credentials: ['B.Tech — IIT Roorkee', 'Software Engineer — HiLabs', 'Backend Engineer — Curie Money'],
    linkedin: 'https://www.linkedin.com/in/usman-khatri-92aa94223/',
    twitter: 'https://x.com/camelCase01',
    github: 'https://github.com/camelCase02',
  },
  {
    name: 'Harshita Singh',
    role: 'UI/UX Designer',
    photo: '/harshita-photo.png',
    bio: 'Harshita shapes Panda\'s product experience with thoughtful UI/UX design. She has shipped feature pages and visual systems at AI startups, turning complex workflows into intuitive, user-first interfaces.',
    credentials: ['B.Tech — IIT Roorkee', 'UI/UX Designer — JUSPAY', 'Product Designer — Trupeer.ai'],
    linkedin: 'https://www.linkedin.com/in/harshita-singh-570561223/',
    twitter: 'https://x.com/harshitasingh81',
  },
];

const TeamCard = ({ member, className = '', delay = 0 }) => (
  <motion.div
    className={`team-card ${className}`}
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
  >
    <div className="team-avatar">
      <img
        src={member.photo}
        alt={member.name}
        onError={(e) => { e.target.onerror = null; e.target.src = '/panda.png'; }}
      />
    </div>
    <h3 className="team-name">{member.name}</h3>
    <p className="team-role">{member.role}</p>
    <p className="team-bio">{member.bio}</p>

    {member.credentials?.length > 0 && (
      <div className="team-credentials">
        {member.credentials.map((c, ci) => (
          <span key={ci} className="team-tag">{c}</span>
        ))}
      </div>
    )}

    <div className="team-socials">
      {member.linkedin && (
        <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="team-social-link" aria-label="LinkedIn">
          LinkedIn
        </a>
      )}
      {member.twitter && (
        <a href={member.twitter} target="_blank" rel="noopener noreferrer" className="team-social-link" aria-label="X">
          X (Twitter)
        </a>
      )}
      {member.github && (
        <a href={member.github} target="_blank" rel="noopener noreferrer" className="team-social-link" aria-label="GitHub">
          GitHub
        </a>
      )}
    </div>
  </motion.div>
);

const About = () => {
  useEffect(() => {
    document.title = "About Steepwind Labs | Panda";
  }, []);

  return (
    <div className="about-page">
      <div className="about-inner">
        
        {/* --- ABOUT US --- */}
        <motion.section 
          className="about-intro-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h1 className="about-main-title">ABOUT US</h1>
          <p className="about-main-subtitle">
            Steepwind Labs is the company. Panda is a product of Steepwind Labs: an easy-to-use voice automation app for Android.
          </p>
        </motion.section>

        <hr className="section-divider" />

        {/* --- GET TO KNOW US (Team Grid) --- */}
        <section className="about-team-section">
          <div className="section-heading-wrapper">
            <div className="section-heading-line"></div>
            <h2 className="section-heading">GET TO KNOW US</h2>
            <div className="section-heading-line"></div>
          </div>

          <motion.div
            className="team-layout"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="team-founder-row">
              <TeamCard member={founder} className="team-card-highlight" delay={0.3} />
            </div>
            <div className="team-grid">
              {team.map((m, i) => (
                <TeamCard key={m.name} member={m} delay={0.45 + (i * 0.15)} />
              ))}
            </div>
          </motion.div>
        </section>

        <hr className="section-divider" />

        {/* --- GET IN TOUCH WITH US --- */}
        <section className="about-contact-section">
          <div className="section-heading-wrapper">
            <div className="section-heading-line"></div>
            <h2 className="section-heading">GET IN TOUCH WITH US</h2>
            <div className="section-heading-line"></div>
          </div>

          <div className="contact-grid">
            <div className="contact-info">
              <h3 className="contact-label">Head Office Address:</h3>
              <p className="contact-value">
                Innov8 Prestige Tech Platina, 11th Floor<br/>
                NO32/2,34/1 KADABISANAHALLI<br/>
                BANGALORE, KAR - 560087
              </p>
              
              <h3 className="contact-label" style={{ marginTop: '32px' }}>For help and support:</h3>
              <p className="contact-value">
                Email : support@heypanda.org
              </p>
            </div>
            
            <div className="contact-map">
               <iframe 
                  title="HQ Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.520448108422!2d77.691763!3d12.9385153!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae13adb5e8ab5b%3A0xc47ff6f7c9e03d7c!2sInnov8%20Prestige%20Tech%20Platina!5e0!3m2!1sen!2sin!4v1716900000000!5m2!1sen!2sin" 
                  width="100%" 
                  height="250" 
                  style={{ border: 0, borderRadius: '12px', filter: 'invert(90%) hue-rotate(180deg)' }} 
                  allowFullScreen="" 
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade">
               </iframe>
            </div>
          </div>
        </section>

      </div>
      <Footer />
    </div>
  );
};

export default About;